import React, { useState } from 'react';
import { Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';
import { CATEGORY_META, groupEventsByBucket } from './timelineUtils';

/**
 * Compact controls + inline event list rendered next to a chart that
 * supports the Activity Timeline overlay.
 *
 * Visual layout:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  [👁 Eventi importanti]  · 4 eventi   [chip filters...]   │
 *   │  ──────────────────────────────────────────────────────── │
 *   │  • 12/03 — Broadcast inviato (4 destinatari) · ...         │
 *   │  • 14/03 — Coupon creato: HAPPY10 · ...                    │
 *   └────────────────────────────────────────────────────────────┘
 */
export function TimelineToggleBar({
  enabled, setEnabled,
  categories, setCategories,
  events,            // filtered events shown for context
  bucketFn,          // for in-range filtering
  chartData, xKey,   // identifies the chart's visible buckets
  expanded, setExpanded,
}) {
  const inRange = React.useMemo(() => {
    if (!events?.length || !chartData?.length || !bucketFn) return [];
    const validBuckets = new Set(chartData.map((d) => d[xKey]));
    const grouped = groupEventsByBucket(events, bucketFn);
    const flat = [];
    grouped.forEach((list, bucket) => {
      if (validBuckets.has(bucket)) flat.push(...list);
    });
    return flat.sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));
  }, [events, chartData, xKey, bucketFn]);

  const toggleCat = (cat) => {
    setCategories((prev) => prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]);
  };

  return (
    <div className="mb-3" data-testid="timeline-toggle-bar">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          data-testid="timeline-toggle"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-medium transition-colors border ${
            enabled
              ? 'bg-[#D44E20]/10 text-[#D44E20] border-[#D44E20]/30'
              : 'bg-slate-50 text-[#64748B] border-slate-200 hover:bg-slate-100'
          }`}
          title="Mostra o nascondi i marker degli eventi importanti"
        >
          {enabled ? <Eye size={12} /> : <EyeOff size={12} />}
          Eventi importanti
        </button>
        {enabled && (
          <span className="text-[#94A3B8]" data-testid="timeline-count">
            · {inRange.length} {inRange.length === 1 ? 'evento' : 'eventi'} nel periodo
          </span>
        )}
        {enabled && Object.entries(CATEGORY_META).map(([cat, meta]) => {
          const on = categories.includes(cat);
          return (
            <button
              key={cat}
              type="button"
              onClick={() => toggleCat(cat)}
              data-testid={`timeline-cat-${cat}`}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border transition-opacity ${
                on ? '' : 'opacity-40 hover:opacity-70'
              }`}
              style={{
                color: meta.color,
                backgroundColor: `${meta.color}12`,
                borderColor: `${meta.color}40`,
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
              {meta.label}
            </button>
          );
        })}
        {enabled && inRange.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            data-testid="timeline-expand"
            className="ml-auto inline-flex items-center gap-1 text-[#64748B] hover:text-[#1A1A1A]"
          >
            {expanded ? 'Nascondi lista' : 'Mostra lista'}
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
      </div>

      {enabled && expanded && inRange.length > 0 && (
        <ul className="mt-3 space-y-1.5 max-h-40 overflow-y-auto pr-1" data-testid="timeline-list">
          {inRange.map((ev) => {
            const meta = CATEGORY_META[ev.category] || CATEGORY_META.manual;
            const Icon = meta.icon;
            const date = ev.occurred_at ? new Date(ev.occurred_at) : null;
            return (
              <li key={ev.id} className="flex items-start gap-2 text-xs">
                <span
                  className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-px"
                  style={{ backgroundColor: `${meta.color}18`, color: meta.color }}
                >
                  <Icon size={11} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1A1A1A] font-medium leading-tight truncate">{ev.title}</p>
                  {ev.description && (
                    <p className="text-[#64748B] truncate">{ev.description}</p>
                  )}
                </div>
                <span className="text-[#94A3B8] text-[10px] tabular-nums flex-shrink-0">
                  {date ? date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) : ''}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
